'use client'

import React, { MouseEventHandler, ReactNode, useState } from 'react'
import { Anim } from "../../ui/Anim"
import { z } from 'zod'
import imgLogoWhite from '@/assets/img/general/logo-blanc.png'
import { messages } from '@/services/validation'
import { postContact } from '@/api/helper'
import avatarFemale from '@/assets/img/avatars/avatar-1.png'
import avatarMale from '@/assets/img/avatars/avatar-2.png'
import avatarNonBinary from '@/assets/img/avatars/avatar-3.png'
import { useSearchParams } from 'next/navigation'
import { getName } from '@/services/aroeven'
import { parsePhone } from '@/services/text'
import { status as s } from '@/services/status'
import Loading from '../../../../app/loading'
import { initRecaptcha } from '@/services/recaptcha/client'
import { getMediaUrl } from '@/services/pathname'
import Staff from '@/components/ui/Staff'
import { checkRecaptcha } from '@/services/recaptcha/server'

type ContactFormProps = {
    aroeven: Aroeven
}

const ContactSchema = z.object({
    firstName: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    lastName: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    department: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    email: z.string().trim().min(1, messages.required).email(messages.email),
    phone: z.string().trim().min(1, messages.required).min(10, messages.tooShort).max(255, messages.tooLong),
    message: z.string().trim().min(1, messages.required),
    target: z.string().nullable()
})

type ContactType = z.infer<typeof ContactSchema>

export function ContactForm({
    aroeven
}: ContactFormProps) {

    const searchParams = useSearchParams()

    const [contact, setContact] = useState({
        firstName: '',
        lastName: '',
        department: '',
        email: '',
        phone: '',
        message: '',
        target: searchParams.has('target') && aroeven.staffs.map(staff => staff.id).includes(searchParams.get('target')) ? searchParams.get('target') : null
    })

    const [hoveredStaff, setHoveredStaff] = useState(null)

    const [errors, setErrors] = useState([])
    const [status, setStatus] = useState(s.idle)    

    async function handleSubmit(e: any) {
        setErrors([])
        try {
            ContactSchema.parse(contact)
        }
        catch (error) {
            setErrors(error.issues)
            window.scrollTo({
                top: ((document.querySelector('.alert') as HTMLElement)?.offsetTop ?? 0) - document.querySelector('header').offsetHeight
            })
            return
        }

        setStatus(s.loading)
        try {
            await initRecaptcha(async (token: string) => {
                try {
                    const result = await checkRecaptcha(token)
                    if (result.success === false) {                        
                        setStatus(s.error)
                        return
                    }                                        
                    await postContact(aroeven, {
                        ...contact,
                        ...contact.target && { target: aroeven.staffs.filter(staff => staff.id === contact.target)[0]?.['@id'] }
                    })
                    setStatus(s.finished)
                    window.scrollTo({
                        top: ((document.querySelector('.alert') as HTMLElement)?.offsetTop ?? 0) - document.querySelector('header').offsetHeight
                    })
                }
                catch (error) {
                    console.log(error.name, error)
                    setStatus(s.error)                
                    window.scrollTo({
                        top: ((document.querySelector('.alert') as HTMLElement)?.offsetTop ?? 0) - document.querySelector('header').offsetHeight
                    })
                    return
                }
            })
        }
        catch (error) {
            console.log(error)
            setStatus(s.error)
            window.scrollTo({
                top: ((document.querySelector('.alert') as HTMLElement)?.offsetTop ?? 0) - document.querySelector('header').offsetHeight
            })
            return
        }
    }

    console.log(errors)

    return (
        <>
            {status === s.loading &&
                <Loading />
            }
            {status === s.finished &&
                <>
                    <section className="texte">
                        <div className="container">
                            <div className="alert alert-success" role="alert">
                                <h4 className="alert-heading">Bien joué !</h4>
                                <p>Votre message a bien été envoyé.</p>
                            </div>
                        </div>
                    </section>
                </>
            }
            {[s.idle, s.error].includes(status) === true &&
                <>
                    <section className="texte">
                        <div className="container">
                            {errors.length > 0 &&
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">Presque !</h4>
                                    <p>Veuillez vérifier les données saisies.</p>
                                </div>
                            }
                            {status === s.error &&
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">Une erreur est survenue.</h4>
                                    <p>Veuillez vérifier les données saisies dans le formulaire ou réessayer plus tard.</p>
                                </div>
                            }
                            {/* <h2>Nous contacter</h2> */}
                            <h4>Votre message</h4>
                            <div className="mt-20">
                                <div className="row x-gap-20 y-gap-20">
                                    <div className="col-md-4">
                                        <div className="form-input">
                                            <input type="text" className={(errors.map(e => e.path[0]).indexOf('firstName') !== -1 ? 'requis' : '')} value={contact.firstName} onChange={(e) => setContact({ ...contact, firstName: e.target.value })} />
                                            <label className="lh-1 text-light-1">
                                                Prénom
                                                <span className="obligatoire">*</span>
                                            </label>
                                        </div>
                                        {errors.map(e => e.path[0]).indexOf('firstName') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'firstName')[0].message}</div>}
                                    </div>
                                    <div className="col-md-5">
                                        <div className="form-input">
                                            <input type="text" className={(errors.map(e => e.path[0]).indexOf('lastName') !== -1 ? 'requis' : '')} value={contact.lastName} onChange={(e) => setContact({ ...contact, lastName: e.target.value })} />
                                            <label className="lh-1 text-light-1">
                                                Nom
                                                <span className="obligatoire">*</span>
                                            </label>
                                        </div>
                                        {errors.map(e => e.path[0]).indexOf('lastName') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'lastName')[0].message}</div>}
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-input">
                                            <input type="text" className={(errors.map(e => e.path[0]).indexOf('department') !== -1 ? 'requis' : '')} value={contact.department} onChange={(e) => setContact({ ...contact, department: e.target.value })} />
                                            <label className="lh-1 text-light-1">
                                                Département
                                                <span className="obligatoire">*</span>
                                            </label>
                                        </div>
                                        {errors.map(e => e.path[0]).indexOf('department') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'department')[0].message}</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-input">
                                            <input type="text" className={(errors.map(e => e.path[0]).indexOf('email') !== -1 ? 'requis' : '')} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                                            <label className="lh-1 text-light-1">
                                                Adresse email
                                                <span className="obligatoire">*</span>
                                            </label>
                                        </div>
                                        {errors.map(e => e.path[0]).indexOf('email') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'email')[0].message}</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-input">
                                            <input type="text" className={(errors.map(e => e.path[0]).indexOf('phone') !== -1 ? 'requis' : '')} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                                            <label className="lh-1 text-light-1">
                                                Téléphone portable
                                                <span className="obligatoire">*</span>
                                            </label>
                                        </div>
                                        {errors.map(e => e.path[0]).indexOf('phone') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'phone')[0].message}</div>}
                                    </div>
                                </div>
                                <div className="row x-gap-20 y-gap-20 mt-20">
                                    <div className="col-12">
                                        <div className="form-input">
                                            <textarea rows={6} value={contact.message} className={(errors.map(e => e.path[0]).indexOf('message') !== -1 ? 'requis' : '')} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
                                            <label className="lh-1 text-light-1">Votre demande <span className="obligatoire">*</span></label>
                                        </div>
                                        {errors.map(e => e.path[0]).indexOf('message') !== -1 && <div className="f-requis">{errors.filter(e => e.path[0] === 'message')[0].message}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <Anim tagName='section' data-anim-wrap className="equipe texte-galerie">
                        <div className="container py-10">
                            {aroeven.staffs.filter(staff => staff.canBeContacted === true).length > 0 &&
                                <>
                                    <div data-anim-child="slide-up delay-1">
                                        <div className="col-auto titre">
                                            <h4>Je souhaite transmettre mon message à :</h4>
                                        </div>
                                    </div>
                                    <div data-anim-child="slide-up delay-2" className="parent flex-wrap mt-40">
                                        <div className={"child tous withhover" + (contact.target === null ? ' active' : '')} onClick={(e) => setContact({ ...contact, target: null })}>
                                            {/* Si vraie photo dans la bubble, afficher le div bubble-back au :hover et .active ET ajouter la classe .photo à bubble team*/}
                                            {/* <div class="bubble-back"> */}
                                            <div className="bubble team">
                                                <div className="align-self-start mx-auto">
                                                    <img src={imgLogoWhite.src} className="centered" />
                                                </div>
                                            </div>
                                            {/* </div> */}
                                            <div className="nom align-self-end mx-auto">
                                                {getName(aroeven)}
                                            </div>
                                            <input type="checkbox" className="d-none" defaultChecked={true} />
                                        </div>
                                        {aroeven.staffs.filter(staff => staff.canBeContacted === true).map((staff, indexStaff) => {
                                            /*return (
                                                <React.Fragment key={'staff-' + indexStaff}>
                                                    <div className={"child" + (contact.target === staff.id ? ' active' : '')} onClick={(e) => setContact({ ...contact, target: staff.id })}>
                                                        <div className={staff.media ? ((contact.target === staff.id || hoveredStaff?.id === staff.id) ? 'bubble-back actif' : 'bubble-back') : ''}>
                                                            <div className={"bubble team" + (staff.media ? ' photo' : '')} {...staff.media && { onMouseEnter: () => setHoveredStaff(staff), onMouseLeave: () => setHoveredStaff(null) }}>
                                                                <div className="align-self-start mx-auto">
                                                                    {staff.media ?
                                                                        <img src={getMediaUrl(staff.media.url)} alt={staff.firstName + ' ' + staff.lastName} />
                                                                        :
                                                                        <img src={({
                                                                            male: avatarMale,
                                                                            female: avatarFemale,
                                                                            non_binary: avatarNonBinary
                                                                        }[staff.gender] ?? avatarMale).src} />
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="nom align-self-end mx-auto">{staff.firstName} {staff.lastName}</div>
                                                        <span className="info">{staff.jobTitle}</span>
                                                    </div>
                                                </React.Fragment>
                                            )*/
                                            return (
                                                <React.Fragment key={'staff-' + indexStaff}>
                                                    <div className={"child withhover" + (contact.target === staff.id ? ' active' : '')} onClick={(e) => setContact({ ...contact, target: staff.id })}>
                                                        <Staff
                                                            staff={staff}
                                                            active={contact.target === staff.id}
                                                        />
                                                    </div>
                                                </React.Fragment>
                                            )
                                        })}
                                    </div>
                                </>
                            }
                            <div data-anim-child="slide-up delay-3" className="d-flex flex-column flex-sm-row justify-content-between mt-40 is-in-view">
                                <div>
                                    <div className="adresse">{getName(aroeven)}</div>
                                    {aroeven.address !== null &&
                                        <p>                                            
                                            <a target='_blank' href={'https://www.google.com/maps?q=' + encodeURIComponent(aroeven.address.address1 + ', ' + aroeven.address.postCode + ' ' + aroeven.address.city)}>
                                                {aroeven.address.address1}
                                                {aroeven.address?.address2 &&
                                                    <>
                                                        <br />
                                                        {aroeven.address?.address2}
                                                    </>
                                                }
                                                <br />
                                                {aroeven.address?.postCode} {aroeven.address?.city}
                                            </a>
                                        </p>
                                    }
                                    {(aroeven.phone || (aroeven.showEmail && aroeven.email)) &&
                                        <p>
                                            {aroeven.phone && <>Tél. <a href={'tel:' + aroeven.phone.replace(/[^0-9+]/g, '')}>{parsePhone(aroeven.phone)}</a></>}
                                            {aroeven.phone && aroeven.email && <br />}
                                            {aroeven.showEmail && aroeven.email && <>{aroeven.email}</>}
                                        </p>
                                    }
                                </div>
                                <div>
                                    <button className="button-primary d-block" disabled={status === s.loading} onClick={handleSubmit}>
                                        <i className="fi fi-bs-paper-plane mr-10" />
                                        Envoyer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Anim>
                </>
            }
        </>
    )
}